import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Database, Activity, Server,
  Download, Mail, CloudUpload, History, CheckCircle2, CheckCircle, XCircle,
  AlertTriangle, Calendar, ScanLine, Plus, X, Trash2,
  LogOut, Eye, HardDrive, RefreshCw,
  CalendarClock, Cloud, Settings, Key, FileCheck,
  UserCheck, Award, ExternalLink, Star, Building2, Smartphone, HeartPulse
} from "lucide-react";
import { API, LOJACK_LOGO } from "../config";
import { PasswordInput } from "../components/shared/PasswordInput";
import { TelcaVoIPBadge } from "../components/shared/TelcaVoIPBadge";
import { SystemHealthBadge } from "../components/admin/SystemHealthBadge";
import { ITPortalI18nProvider, useITPortalI18n } from "../i18n/ITPortalI18nContext";
import { useDeferredEffect } from "../hooks/useWalletEffects";
import { SecurityTab } from "../components/it-portal/SecurityTab";
import { BackupTab } from "../components/it-portal/BackupTab";
import { CommunicationsTab } from "../components/it-portal/CommunicationsTab";
import { AppleWalletTab } from "../components/it-portal/AppleWalletTab";
import { HealthCenterTab } from "../components/it-portal/HealthCenterTab";
import { InfraResourcesPanel } from "../components/shared/InfraResourcesPanel";
import { Gauge } from "lucide-react";

/* ─── IT Portal Main ─── */
const ITPortalPageInner = () => {
  const { t, locale, setLocale } = useITPortalI18n();
  const [authenticated, setAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [tab, setTab] = useState("resources");

  // Infrastructure metrics

  // S3 Storage
  const [s3Config, setS3Config] = useState({ bucket_name: "", region: "eu-south-1", access_key: "", secret_key: "", prefix: "contracts/", enabled: true });
  const [s3Status, setS3Status] = useState(null);
  const [s3Logs, setS3Logs] = useState([]);
  const [s3Saving, setS3Saving] = useState(false);
  const [s3Testing, setS3Testing] = useState(false);

  // Security data
  const [assessments, setAssessments] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupSchedule, setBackupSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  // Backup operations
  const [backupTab, setBackupTab] = useState("main");
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupEmailing, setBackupEmailing] = useState(false);
  const [cloudConfig, setCloudConfig] = useState({ provider: "aws_s3", endpoint_url: "", bucket_name: "", access_key: "", secret_key: "", region: "eu-west-1", enabled: false });
  const [savingCloud, setSavingCloud] = useState(false);
  const [uploadingCloud, setUploadingCloud] = useState(false);

  // VA form
  const [showAddVA, setShowAddVA] = useState(false);
  const [vaForm, setVAForm] = useState({ scan_date: '', performed_by: '', status: 'passed', critical: 0, high: 0, medium: 0, low: 0, notes: '' });
  const [vaFile, setVAFile] = useState(null);
  const [submittingVA, setSubmittingVA] = useState(false);

  // Change password
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  // Forgot password (login screen, self-service reset).
  // Mirrors LoginPage UX: calls POST /api/auth/admin/forgot-password
  // (anti-enumeration: always 200 with generic message) and shows a
  // submitted-banner regardless of whether the email is registered.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);

  // PDF language selector
  const [pdfLangOpen, setPdfLangOpen] = useState(false);
  const [pdfLang, setPdfLang] = useState("IT");
  const [translatingPdf, setTranslatingPdf] = useState(false);
  const PDF_LANGS = [
    { code: "IT", label: "Italiano", flag: "\u{1F1EE}\u{1F1F9}" }, { code: "EN", label: "English", flag: "\u{1F1EC}\u{1F1E7}" },
    { code: "FR", label: "Fran\u00e7ais", flag: "\u{1F1EB}\u{1F1F7}" }, { code: "ES", label: "Espa\u00f1ol", flag: "\u{1F1EA}\u{1F1F8}" },
    { code: "DE", label: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" }, { code: "PT", label: "Portugu\u00eas", flag: "\u{1F1F5}\u{1F1F9}" },
    { code: "NL", label: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}" }, { code: "PL", label: "Polski", flag: "\u{1F1F5}\u{1F1F1}" },
    { code: "SE", label: "Svenska", flag: "\u{1F1F8}\u{1F1EA}" }, { code: "NO", label: "Norsk", flag: "\u{1F1F3}\u{1F1F4}" },
    { code: "FI", label: "Suomi", flag: "\u{1F1EB}\u{1F1EE}" }, { code: "DK", label: "Dansk", flag: "\u{1F1E9}\u{1F1F0}" },
    { code: "CZ", label: "Ce\u0161tina", flag: "\u{1F1E8}\u{1F1FF}" }, { code: "GR", label: "Ellinika", flag: "\u{1F1EC}\u{1F1F7}" },
    { code: "CH", label: "Deutsch (CH)", flag: "\u{1F1E8}\u{1F1ED}" }, { code: "BE", label: "Fran\u00e7ais (BE)", flag: "\u{1F1E7}\u{1F1EA}" },
    { code: "IE", label: "English (IE)", flag: "\u{1F1EE}\u{1F1EA}" }, { code: "UK", label: "English (UK)", flag: "\u{1F1EC}\u{1F1E7}" },
  ];

  useEffect(() => {
    const token = localStorage.getItem("it_token");
    if (token) {
      // Verify token is still valid and get correct user name
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const role = res.data?.role;
          if (role === "it" || role === "super_admin" || role === "admin") {
            localStorage.setItem("it_role", role);
            setAuthenticated(true);
            setUserName(res.data?.name || "IT");
            localStorage.setItem("it_user_name", res.data?.name || "IT");
          } else {
            localStorage.removeItem("it_token");
            localStorage.removeItem("it_user_name");
          }
        })
        .catch(() => {
          localStorage.removeItem("it_token");
          localStorage.removeItem("it_user_name");
        });
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(""); setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword });
      if (res.data.user?.role !== "it" && res.data.user?.role !== "super_admin" && res.data.user?.role !== "admin") {
        setLoginError(t.it_err_role_not_allowed);
        setLoginLoading(false); return;
      }
      localStorage.setItem("it_token", res.data.token);
      localStorage.setItem("it_user_name", res.data.user?.name || "IT");
      setAuthenticated(true); setUserName(res.data.user?.name || "IT");
    } catch { setLoginError(t.it_err_invalid_credentials); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("it_token"); localStorage.removeItem("it_user_name");
    setAuthenticated(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { toast.error(t.it_pw_mismatch); return; }
    if (pwForm.newPw.length < 6) { toast.error(t.it_pw_too_short); return; }
    setChangingPw(true);
    try {
      const token = localStorage.getItem("it_token");
      await axios.post(`${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.newPw },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t.it_pw_updated);
      setShowChangePw(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || t.it_pw_update_error); }
    finally { setChangingPw(false); }
  };

  const handleDownloadSecurityPdf = async () => {
    setTranslatingPdf(true);
    setPdfLangOpen(false);
    try {
      const response = await axios.get(`${API}/security/report-pdf?lang=${pdfLang}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TelcaVoIP_Security_${pdfLang}_${new Date().toISOString().slice(0,7)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      if (pdfLang !== 'IT') toast.success(`PDF generato in ${PDF_LANGS.find(l => l.code === pdfLang)?.label || pdfLang}`);
    } catch { toast.error(t.toast_pdf_err); }
    finally { setTranslatingPdf(false); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vaRes, bsRes, bhRes, schedRes, cloudRes, s3CfgRes, s3LogsRes] = await Promise.all([
        axios.get(`${API}/security/vulnerability-assessments`),
        axios.get(`${API}/backup/status`),
        axios.get(`${API}/backup/history`),
        axios.get(`${API}/backup/schedule`),
        axios.get(`${API}/backup/cloud-config`),
        axios.get(`${API}/s3/config`),
        axios.get(`${API}/s3/upload-logs`),
      ]);
      setAssessments(vaRes.data);
      setBackupStatus(bsRes.data);
      setBackupHistory(bhRes.data);
      setBackupSchedule(schedRes.data);
      setCloudConfig(prev => ({ ...prev, ...cloudRes.data }));
      setS3Status(s3CfgRes.data);
      setS3Logs(s3LogsRes.data.logs || []);
    } catch { /* */ }
    finally { setLoading(false); }
  };

  useDeferredEffect(() => { if (authenticated) fetchData(); }, [authenticated]);

  const downloadBackup = async () => {
    try {
      setBackupDownloading(true);
      const res = await axios.get(`${API}/backup/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `lojack_backup_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(t.toast_backup_downloaded); fetchData();
    } catch { toast.error(t.toast_backup_download_err); }
    finally { setBackupDownloading(false); }
  };

  const sendBackupEmail = async () => {
    try {
      setBackupEmailing(true);
      const res = await axios.post(`${API}/backup/send-email`);
      toast.success(t.toast_backup_sent.replace('{email}', res.data.email)); fetchData();
    } catch { toast.error(t.toast_backup_send_err); }
    finally { setBackupEmailing(false); }
  };

  const toggleAutoBackup = async () => {
    try {
      const newState = !backupSchedule?.enabled;
      const days = backupSchedule?.interval_days || 7;
      await axios.post(`${API}/backup/schedule?enabled=${newState}&interval_days=${days}`);
      setBackupSchedule(prev => ({ ...prev, enabled: newState }));
      toast.success(newState ? t.toast_auto_backup_on.replace('{n}', String(days)) : t.toast_auto_backup_off);
    } catch { toast.error(t.toast_backup_cfg_err); }
  };

  const updateBackupInterval = async (days) => {
    try {
      await axios.post(`${API}/backup/schedule?enabled=true&interval_days=${days}`);
      setBackupSchedule({ enabled: true, interval_days: days });
      const labels = {1: t.toast_backup_interval_label_1, 7: t.toast_backup_interval_label_7, 15: t.toast_backup_interval_label_15, 30: t.toast_backup_interval_label_30};
      toast.success(t.toast_auto_backup_set.replace('{label}', labels[days] || `${days}`));
    } catch { toast.error(t.toast_backup_interval_err); }
  };

  const saveCloudConfig = async () => {
    setSavingCloud(true);
    try { await axios.post(`${API}/backup/cloud-config`, cloudConfig); toast.success(t.toast_cloud_saved); }
    catch { toast.error(t.toast_cloud_save_err); }
    finally { setSavingCloud(false); }
  };

  const uploadToCloud = async () => {
    setUploadingCloud(true);
    try {
      const res = await axios.post(`${API}/backup/upload-cloud`);
      if (res.data.error) toast.error(res.data.error);
      else { toast.success(res.data.message); fetchData(); }
    } catch { toast.error(t.toast_cloud_upload_err); }
    finally { setUploadingCloud(false); }
  };

  const handleAddVA = async (e) => {
    e.preventDefault(); setSubmittingVA(true);
    try {
      const fd = new FormData();
      Object.entries(vaForm).forEach(([k, v]) => fd.append(k, v));
      if (vaFile) fd.append('report_file', vaFile);
      await axios.post(`${API}/security/vulnerability-assessments`, fd);
      toast.success(t.toast_va_registered);
      setShowAddVA(false);
      setVAForm({ scan_date: '', performed_by: '', status: 'passed', critical: 0, high: 0, medium: 0, low: 0, notes: '' });
      setVAFile(null); fetchData();
    } catch { toast.error(t.va_generic_error); }
    finally { setSubmittingVA(false); }
  };

  const deleteVA = async (id) => {
    if (!window.confirm(t.va_delete_confirm)) return;
    try { await axios.delete(`${API}/security/vulnerability-assessments/${id}`); toast.success(t.va_deleted); fetchData(); } catch { toast.error(t.va_generic_error); }
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center p-6">
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-8 text-center max-w-sm w-full">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-10" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-1"><Server size={18} className="text-lojack-blue-400" /><h2 className="text-white text-lg font-bold">IT Portal</h2></div>
          <p className="text-gray-500 text-sm mb-6">{t.it_login_subtitle}</p>
          {loginError && <div className="bg-red-900/20 border border-red-900/30 rounded-xl p-3 mb-4"><p className="text-red-400 text-sm">{loginError}</p></div>}
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" value={loginEmail} onChange={e => { setLoginEmail(e.target.value); setLoginError(""); }}
              placeholder={t.it_login_email_placeholder} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" required data-testid="it-login-email" />
            <PasswordInput value={loginPassword} onChange={e => { setLoginPassword(e.target.value); setLoginError(""); }}
              placeholder={t.it_login_password_placeholder} className="w-full px-4 py-3 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" required data-testid="it-login-password" />
            <button type="submit" disabled={loginLoading}
              className="w-full py-3 bg-lojack-blue-600 text-white rounded-xl font-medium hover:bg-lojack-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="it-login-submit">
              {loginLoading ? t.it_login_verifying : t.it_login_submit}
            </button>
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => { setForgotEmail(loginEmail); setForgotOpen(true); setForgotSubmitted(false); }}
                className="text-xs text-gray-500 hover:text-lojack-blue-400 transition underline-offset-2 hover:underline"
                data-testid="it-forgot-password-link"
              >
                {t.it_forgot_pw_link}
              </button>
            </div>
          </form>
          <div className="mt-6 text-center"><TelcaVoIPBadge /></div>
        </div>
        {/* Forgot password modal — mirrors the LoginPage flow. The
            backend endpoint POST /api/auth/admin/forgot-password
            queries db.admin_users which already contains IT users
            (role='it'), so no extra backend work is required. */}
        {forgotOpen && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setForgotOpen(false)}
            data-testid="it-forgot-modal-overlay"
          >
            <div
              className="bg-[#1A1A1A] rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden"
              onClick={e => e.stopPropagation()}
              data-testid="it-forgot-modal"
            >
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Mail size={18} className="text-lojack-blue-400" />
                  {t.it_forgot_pw_title}
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  {t.it_forgot_pw_subtitle}
                </p>
              </div>
              <div className="p-6">
                {forgotSubmitted ? (
                  <div className="space-y-4">
                    <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-4 text-sm text-emerald-200" data-testid="it-forgot-submitted-banner">
                      {t.it_forgot_pw_submitted}
                    </div>
                    <button
                      onClick={() => setForgotOpen(false)}
                      className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg"
                      data-testid="it-forgot-close-btn"
                    >
                      {t.it_forgot_pw_close}
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={async e => {
                      e.preventDefault();
                      if (!forgotEmail.trim()) return;
                      setForgotSending(true);
                      try {
                        await axios.post(`${API}/auth/admin/forgot-password`, { email: forgotEmail.trim() });
                        setForgotSubmitted(true);
                      } catch (err) {
                        if (err.response?.status >= 500) {
                          toast.error(t.it_forgot_pw_service_error);
                        } else {
                          // Anti-enumeration: surface generic submitted
                          // banner even if the backend rejected the
                          // payload for other reasons (4xx).
                          setForgotSubmitted(true);
                        }
                      } finally {
                        setForgotSending(false);
                      }
                    }}
                    className="space-y-4"
                    data-testid="it-forgot-form"
                  >
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Email</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        required
                        autoFocus
                        placeholder={t.it_login_email_placeholder}
                        className="w-full bg-[#0A0A0A] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-lojack-blue-500 focus:outline-none text-sm"
                        data-testid="it-forgot-email-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setForgotOpen(false)}
                        className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg"
                        data-testid="it-forgot-cancel-btn"
                      >
                        {t.it_forgot_pw_cancel}
                      </button>
                      <button
                        type="submit"
                        disabled={forgotSending || !forgotEmail.trim()}
                        className="flex-1 px-4 py-2.5 bg-lojack-blue-600 hover:bg-lojack-blue-700 text-white text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="it-forgot-submit-btn"
                      >
                        {forgotSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : t.it_forgot_pw_submit}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const latestVA = assessments.length > 0 ? assessments[0] : null;
  const vaStatusColor = latestVA?.status === 'passed' ? 'text-lojack-blue-400' : latestVA?.status === 'warning' ? 'text-lojack-clause-400' : 'text-red-400';
  const vaStatusLabel = latestVA?.status === 'passed' ? t.va_status_passed : latestVA?.status === 'warning' ? t.va_status_warning : t.va_status_critical;

  const TABS = [
    { key: "resources", label: t.tab_resources, icon: Gauge },
    { key: "s3", label: t.tab_s3, icon: HardDrive },
    { key: "security", label: t.tab_security, icon: Shield },
    { key: "backup", label: t.tab_backup, icon: Database },
    { key: "apple_wallet", label: t.tab_apple_wallet, icon: Smartphone },
    { key: "health", label: t.tab_health, icon: HeartPulse },
    { key: "communications", label: t.tab_communications, icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-lojack-black" data-testid="it-portal-page">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-9" />
            </div>
            <div className="h-6 w-px bg-gray-700" />
            <div className="flex items-center gap-2"><Server size={16} className="text-lojack-blue-400" /><span className="text-white font-semibold text-sm">{t.page_title}</span></div>
          </div>
          <div className="flex items-center gap-4">
            {/* Locale switcher — auto-detected from navigator.language
                on first visit, then persisted in localStorage. */}
            <div className="flex items-center gap-1 text-xs" data-testid="it-locale-switcher">
              {["IT", "EN"].map(code => (
                <button
                  key={code}
                  onClick={() => { setLocale(code); axios.patch(`${API}/auth/me/language`, { language: code.toLowerCase() }, { headers: { Authorization: `Bearer ${localStorage.getItem("it_token")}` } }).catch(() => {}); }}
                  className={`px-2 py-1 rounded font-mono transition-colors ${
                    locale === code
                      ? "bg-lojack-blue-600/20 text-lojack-blue-300 border border-lojack-blue-500/40"
                      : "text-gray-500 hover:text-white border border-transparent"
                  }`}
                  data-testid={`it-locale-${code}`}
                >
                  {code}
                </button>
              ))}
            </div>
            <span className="text-gray-400 text-sm">{userName}</span>
            <SystemHealthBadge
              compact
              clickable={false}
              authHeaderOverride={{ Authorization: `Bearer ${localStorage.getItem('it_token') || ''}` }}
              roleOverride="it"
            />
            <button onClick={() => setShowChangePw(true)} className="flex items-center gap-2 text-gray-500 hover:text-lojack-blue-400 text-sm transition-colors" data-testid="it-change-pw-btn"><Key size={16} />{t.change_password}</button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-500 hover:text-white text-sm transition-colors" data-testid="it-logout-btn"><LogOut size={16} />{t.logout}</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${tab === t.key ? 'bg-lojack-blue-600 text-white' : 'bg-[#111] border border-gray-800 text-gray-400 hover:text-white hover:border-gray-600'}`}
              data-testid={`it-tab-${t.key}`}>
              <t.icon size={16} />{t.label}
            </button>
          ))}
        </div>

        {loading && <div className="flex justify-center py-20"><div className="spinner" /></div>}

        {!loading && (
          <>
            {/* COMMUNICATIONS TAB */}
            {tab === "communications" && <CommunicationsTab />}

            {/* S3 STORAGE TAB */}
            {tab === "s3" && (
              <div className="space-y-6 animate-fade-in" data-testid="it-s3-tab">
                {/* S3 Status */}
                <div className={`bg-[#111] border rounded-2xl p-6 ${s3Status?.enabled ? 'border-lojack-blue-600/30' : 'border-lojack-clause-600/30'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s3Status?.bucket_name ? 'bg-lojack-blue-500/10' : 'bg-lojack-clause-500/10'}`}>
                        <HardDrive size={24} className={s3Status?.bucket_name ? 'text-lojack-blue-400' : 'text-lojack-clause-400'} />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{s3Status?.bucket_name ? t.s3_bucket_configured : t.s3_bucket_not_configured}</h3>
                        <p className="text-gray-400 text-sm">{s3Status?.bucket_name ? `Bucket: ${s3Status.bucket_name} | Region: ${s3Status.region}` : t.s3_enter_creds}</p>
                      </div>
                    </div>
                    <button onClick={async () => { setS3Testing(true); try { await axios.post(`${API}/s3/test-connection`); toast.success(t.s3_conn_ok); } catch(e) { toast.error(e.response?.data?.detail || t.s3_conn_fail); } finally { setS3Testing(false); }}} disabled={s3Testing || !s3Status?.bucket_name} className="bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="s3-test-btn">
                      {s3Testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle size={14} />}{t.s3_test_btn}
                    </button>
                  </div>
                </div>

                {/* S3 Config Form */}
                <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Settings size={16} className="text-lojack-blue-400" />{t.s3_config_title}</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-gray-400 text-xs">{t.s3_bucket_name_label}</label>
                        <input value={s3Config.bucket_name} onChange={e => setS3Config(p => ({...p, bucket_name: e.target.value}))} placeholder="lojack-contracts-prod" className="w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" data-testid="s3-bucket-name" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs">{t.s3_region_label}</label>
                        <input value={s3Config.region} onChange={e => setS3Config(p => ({...p, region: e.target.value}))} placeholder="eu-south-1" className="w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" data-testid="s3-region" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-gray-400 text-xs">{t.s3_access_key_label}</label>
                        <input value={s3Config.access_key} onChange={e => setS3Config(p => ({...p, access_key: e.target.value}))} placeholder="AKIA..." className="w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm font-mono" data-testid="s3-access-key" />
                      </div>
                      <div>
                        <label className="text-gray-400 text-xs">{t.s3_secret_key_label}</label>
                        <input type="password" value={s3Config.secret_key} onChange={e => setS3Config(p => ({...p, secret_key: e.target.value}))} placeholder="••••••••••••" className="w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm font-mono" data-testid="s3-secret-key" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-gray-400 text-xs">{t.s3_prefix_label}</label>
                        <input value={s3Config.prefix} onChange={e => setS3Config(p => ({...p, prefix: e.target.value}))} placeholder="contracts/" className="w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" data-testid="s3-prefix" />
                        <p className="text-gray-600 text-xs mt-1">{t.s3_prefix_help}</p>
                      </div>
                      <div className="flex items-end">
                        <button onClick={async () => { setS3Saving(true); try { await axios.put(`${API}/s3/config`, s3Config); toast.success(t.s3_save_ok); fetchData(); } catch { toast.error(t.s3_save_error); } finally { setS3Saving(false); }}} disabled={s3Saving} className="bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2" data-testid="s3-save-btn">
                          {s3Saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Settings size={14} />}{t.s3_save_btn}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* S3 Upload Logs */}
                <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Activity size={16} className="text-lojack-blue-400" />{t.s3_history_title}</h3>
                  {s3Logs.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {s3Logs.map((log, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-800/30 rounded-xl px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.status === 'success' ? 'bg-lojack-blue-500/10' : 'bg-red-500/10'}`}>
                              {log.status === 'success' ? <CheckCircle size={14} className="text-lojack-blue-400" /> : <XCircle size={14} className="text-red-400" />}
                            </div>
                            <div>
                              <p className="text-white text-sm">{log.filename || log.customer_id?.substring(0, 8) + "..."}</p>
                              <p className="text-gray-500 text-xs">{log.uploaded_at && new Date(log.uploaded_at).toLocaleString(locale === 'IT' ? 'it-IT' : 'en-GB')}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${log.status === 'success' ? 'bg-lojack-blue-500/10 text-lojack-blue-400' : 'bg-red-500/10 text-red-400'}`}>{log.status === 'success' ? 'OK' : t.va_generic_error.toUpperCase()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <HardDrive size={36} className="mx-auto mb-3 text-gray-700" />
                      <p className="text-gray-500 text-sm">{t.s3_no_uploads}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SECURITY TAB */}
            {tab === "security" && (
              <SecurityTab
                assessments={assessments}
                latestVA={latestVA}
                vaStatusColor={vaStatusColor}
                vaStatusLabel={vaStatusLabel}
                deleteVA={deleteVA}
                setShowAddVA={setShowAddVA}
                PDF_LANGS={PDF_LANGS}
                pdfLang={pdfLang}
                setPdfLang={setPdfLang}
                pdfLangOpen={pdfLangOpen}
                setPdfLangOpen={setPdfLangOpen}
                handleDownloadSecurityPdf={handleDownloadSecurityPdf}
                translatingPdf={translatingPdf}
              />
            )}

            {tab === "backup" && (
              <BackupTab
                backupStatus={backupStatus}
                backupHistory={backupHistory}
                backupTab={backupTab}
                setBackupTab={setBackupTab}
                downloadBackup={downloadBackup}
                backupDownloading={backupDownloading}
                sendBackupEmail={sendBackupEmail}
                backupEmailing={backupEmailing}
                uploadToCloud={uploadToCloud}
                uploadingCloud={uploadingCloud}
                toggleAutoBackup={toggleAutoBackup}
                backupSchedule={backupSchedule}
                updateBackupInterval={updateBackupInterval}
                cloudConfig={cloudConfig}
                setCloudConfig={setCloudConfig}
                saveCloudConfig={saveCloudConfig}
                savingCloud={savingCloud}
              />
            )}

            {/* APPLE WALLET TAB */}
            {tab === "apple_wallet" && <AppleWalletTab />}
            {tab === "health" && <HealthCenterTab />}
            {tab === "resources" && <InfraResourcesPanel token={localStorage.getItem("it_token")} canEdit={localStorage.getItem("it_role") === "super_admin"} lang={locale === "EN" ? "en" : "it"} />}

            {/* AI MONITOR TAB */}
          </>
        )}
      </div>

      {/* Add VA Modal */}
      {showAddVA && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={() => setShowAddVA(false)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white flex items-center gap-2"><ScanLine size={20} className="text-lojack-blue-400" />{t.va_modal_title}</h2>
              <button onClick={() => setShowAddVA(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleAddVA} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-gray-400 text-xs">{t.va_modal_date}</label><input type="date" value={vaForm.scan_date} onChange={e => setVAForm({...vaForm, scan_date: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required /></div>
                <div><label className="text-gray-400 text-xs">{t.va_modal_performed_by}</label><input value={vaForm.performed_by} onChange={e => setVAForm({...vaForm, performed_by: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required /></div>
              </div>
              <div><label className="text-gray-400 text-xs">{t.va_modal_status}</label>
                <select value={vaForm.status} onChange={e => setVAForm({...vaForm, status: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none">
                  <option value="passed">{t.va_modal_pass}</option><option value="warning">{t.va_modal_warning}</option><option value="failed">{t.va_modal_critical}</option>
                </select>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[{k:'critical',l:t.va_critical_count},{k:'high',l:t.va_high_count},{k:'medium',l:t.va_medium_count},{k:'low',l:t.va_low_count}].map(f => (
                  <div key={f.k}><label className="text-gray-400 text-xs">{f.l}</label><input type="number" min="0" value={vaForm[f.k]} onChange={e => setVAForm({...vaForm, [f.k]: parseInt(e.target.value)||0})} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white text-center focus:outline-none" /></div>
                ))}
              </div>
              <div><label className="text-gray-400 text-xs">{t.va_modal_notes}</label><textarea value={vaForm.notes} onChange={e => setVAForm({...vaForm, notes: e.target.value})} rows={2} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none resize-none" /></div>
              <button type="submit" disabled={submittingVA} className="w-full py-3 bg-lojack-blue-600 hover:bg-lojack-blue-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50">{submittingVA ? t.va_modal_saving : t.va_modal_save}</button>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePw && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={() => setShowChangePw(false)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white flex items-center gap-2"><Key size={18} className="text-lojack-blue-400" />{t.pw_modal_title}</h2>
              <button onClick={() => setShowChangePw(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleChangePassword} className="p-5 space-y-4">
              <div>
                <label className="text-gray-400 text-xs">{t.pw_current}</label>
                <PasswordInput value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})}
                  className="w-full mt-1 px-4 py-3 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required data-testid="it-pw-current" />
              </div>
              <div>
                <label className="text-gray-400 text-xs">{t.pw_new}</label>
                <PasswordInput value={pwForm.newPw} onChange={e => setPwForm({...pwForm, newPw: e.target.value})}
                  className="w-full mt-1 px-4 py-3 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required minLength={6} data-testid="it-pw-new" />
              </div>
              <div>
                <label className="text-gray-400 text-xs">{t.pw_confirm}</label>
                <PasswordInput value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                  className="w-full mt-1 px-4 py-3 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required minLength={6} data-testid="it-pw-confirm" />
              </div>
              <button type="submit" disabled={changingPw}
                className="w-full py-3 bg-lojack-blue-600 text-white rounded-xl font-medium hover:bg-lojack-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="it-pw-submit">
                {changingPw ? t.pw_saving : t.pw_save}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ITPortalPage() {
  return (
    <ITPortalI18nProvider>
      <ITPortalPageInner />
    </ITPortalI18nProvider>
  );
}
